import { Component, Input, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartOptions, ChartType } from 'chart.js';
import { PerformanceFrame } from '../../../../models/session.model';

@Component({
  selector: 'app-session-performance-chart-panel',
  standalone: true,
  imports: [CommonModule, CdkDrag, BaseChartDirective],
  template: `
    <div cdkDrag
         class="absolute top-20 left-4 bg-black border border-zaxis-border/90 border-zaxis-accent/50 rounded-none p-4 w-[380px] max-w-[calc(100vw-32px)] shadow-pixel z-20 animate-panel-in flex flex-col gap-3 cursor-move">
      <div class="flex justify-between items-start">
        <span class="text-xs text-gray-400 uppercase tracking-wider font-bold pt-1"><i class="ph ph-lightning"></i> Performance Timeline</span>
        <button (click)="closed.emit()" class="text-gray-400 hover:text-white w-6 h-6 flex items-center justify-center rounded-none bg-white/5 hover:bg-white/10 transition-colors flex-shrink-0">&times;</button>
      </div>

      <!-- Live stats row -->
      <div class="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-white bg-black/30 px-3 py-2 rounded-none border border-zaxis-border">
        <span *ngIf="currentFrame">FPS: <span class="font-bold font-mono" [class]="currentFrame.fps >= 60 ? 'text-green-400' : currentFrame.fps >= 30 ? 'text-yellow-400' : 'text-red-400'">{{ currentFrame.fps | number:'1.0-1' }}</span></span>
        <span *ngIf="currentFrame">CPU: <span class="font-bold font-mono text-purple-400">{{ currentFrame.cpuLevel || 0 }}</span></span>
        <span *ngIf="currentFrame">GPU: <span class="font-bold font-mono text-pink-400">{{ currentFrame.gpuLevel || 0 }}</span></span>
        <span *ngIf="currentFrame">RAM: <span class="font-bold font-mono text-emerald-400">{{ currentFrame.ramUsageMb || 0 }} MB</span></span>
        <span *ngIf="currentFrame && currentFrame.batteryLevel !== undefined && currentFrame.batteryLevel >= 0">
          Bat: <span class="font-bold font-mono text-green-400">{{ currentFrame.batteryLevel | number:'1.0-0' }}%</span>
        </span>
      </div>

      <!-- Chart -->
      <div class="h-[180px] w-full mt-2 overflow-x-auto overflow-y-hidden custom-scrollbar">
        <div [style.min-width.px]="chartWidth" class="h-full relative w-full">
          <canvas baseChart [data]="chartData" [options]="chartOptions" [type]="chartType"></canvas>
        </div>
      </div>
    </div>
  `,
  styles: [`
    @keyframes panelIn {
      from { transform: translateX(20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    .animate-panel-in { animation: panelIn 0.2s ease-out; }
    .custom-scrollbar::-webkit-scrollbar { width: 4px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
  `]
})
export class SessionPerformanceChartPanelComponent implements OnChanges {
  @Input() frames: PerformanceFrame[] = [];
  @Input() currentFrame: PerformanceFrame | null = null;
  @Input() currentIndex = -1;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  // ponytail: closed emitted instead of two-way binding — parent controls visibility
  @Output() closed = new EventEmitter<void>();

  chartType = 'line' as const;
  chartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };
  chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    elements: {
      point: { radius: 0, hitRadius: 10, hoverRadius: 4 },
      line: { tension: 0.3, borderJoinStyle: 'round' }
    },
    scales: {
      x: { display: true, grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#a1a1aa', maxTicksLimit: 8, font: { size: 9 } } },
      y: { type: 'linear', display: true, position: 'left', grid: { color: 'rgba(255,255,255,0.05)' }, ticks: { color: '#3b82f6', font: { size: 9 } } },
      y1: { type: 'linear', display: false, position: 'right' },
      y2: { type: 'linear', display: false, position: 'right' }
    },
    plugins: {
      legend: { display: true, position: 'bottom', labels: { color: '#a1a1aa', font: { size: 10 }, usePointStyle: true, boxWidth: 6 } },
      tooltip: { enabled: true, mode: 'index', intersect: false }
    }
  };

  get chartWidth(): number {
    return Math.max(380, (this.frames?.length || 0) * 3);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['frames'] && this.frames?.length > 0) {
      this.initChartData();
    }
    if (this.frames?.length > 0) {
      this.updateChartDataToCurrentTime();
    }
  }

  private initChartData() {
    const formatTime = (ms: number) => {
      const s = Math.floor(ms / 1000);
      const m = Math.floor(s / 60);
      return m > 0 ? `${m}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
    };

    this.chartData = {
      labels: this.frames.map(f => formatTime(f.timestampOffsetMs)),
      datasets: [
        { data: [], label: 'FPS', borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,0.1)', fill: true, yAxisID: 'y', borderWidth: 2 },
        { data: [], label: 'CPU Time (ms)', borderColor: '#a855f7', backgroundColor: 'transparent', yAxisID: 'y1', borderWidth: 1.5, borderDash: [5, 5] },
        { data: [], label: 'GPU Time (ms)', borderColor: '#ec4899', backgroundColor: 'transparent', yAxisID: 'y1', borderWidth: 1.5, borderDash: [5, 5] },
        { data: [], label: 'RAM (MB)', borderColor: '#10b981', backgroundColor: 'transparent', yAxisID: 'y2', borderWidth: 2 }
      ]
    };
  }

  private updateChartDataToCurrentTime() {
    const max = this.currentIndex;
    
    if (!this.chartData || !this.chartData.datasets || this.chartData.datasets.length < 4) {
      return;
    }

    this.chartData.datasets[0].data = this.frames.map((f, i) => i <= max ? f.fps : null);
    this.chartData.datasets[1].data = this.frames.map((f, i) => i <= max ? (f.cpuCycleTimeMs || null) : null);
    this.chartData.datasets[2].data = this.frames.map((f, i) => i <= max ? (f.gpuCycleTimeMs || null) : null);
    this.chartData.datasets[3].data = this.frames.map((f, i) => i <= max ? (f.ramUsageMb || null) : null);
    
    if (this.chart) {
      this.chart.update();
    }
  }
}

